'use client';
import type React from 'react';
import { useEffect, useState } from 'react';
import Modal from '@/components/modal/Modal';
import type { ModalEmailProps } from '@/components/modal/types';
import { Button } from '@/components/button/Button';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { Input } from '@/components/input/Input';
import { Headline } from '@/components/headline/Headline';
import Icon from '@/components/icon/Icon';
import * as Yup from 'yup';
import { createPortal } from 'react-dom';
import { useTranslations } from '@/lib';
import { submitLeadFormData } from '@/lib/lead-submission';

const emailValidationSchema = Yup.object({
  email: Yup.string()
    .email('Bitte geben Sie eine gültige E-Mail-Adresse ein')
    .required('E-Mail-Adresse ist erforderlich'),
});

const EmailSuccessModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslations();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-md overflow-hidden rounded-xl bg-white p-8 text-white shadow-lg">
        <div className="from-blue-600/20 absolute inset-0 z-0 bg-gradient-to-br to-slate-800"></div>
        <div className="from-blue-400/20 absolute inset-0 z-0 bg-gradient-to-tl to-transparent"></div>

        {/* Content wrapper with higher z-index */}
        <div className="relative z-10 text-center">
          <Icon
            name={'circle-check'}
            color={'white'}
            fill="green"
            size={'41px'}
            className="mx-auto"
          />

          <h2 className="mb-4 text-center text-[26px] font-[400] text-black">
            {t('modal.success')}
          </h2>
          <p className="mb-6 text-center text-[16px] font-[400] text-neutral-500">
            {t('modal.willInformYouWhenAvailableWatchlist')}
          </p>
          <Button
            variant="primary"
            onClick={onClose}
            className={`w-full justify-center`}
            name={t('forms.close')}
          />
        </div>
      </div>
    </div>
  );
};

const EmailModal: React.FC<ModalEmailProps> = ({
  size = 'small',
  ariaLabel = 'Email Watchlist Modal',
  mainHeadline,
  description,
  onOpen,
  children,
  ...props
}) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [mounted, setMounted] = useState(false);
  const CAMPAIGN_KEY = 'mkt26_smzh';
  const [pathname, setPathname] = useState<string>('');

  const { t } = useTranslations();

  useEffect(() => {
    setPathname(window.location.href); // runs only in the browser
  }, []);

  useEffect(() => setMounted(true), []);
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    onOpen(false);
  };

  const submitEmailData = async (email: string) => {
    const fd = new FormData();

    const emailTrimmed = (email || '').trim();

    // Required / safe fields
    fd.append('Email', emailTrimmed);
    fd.append('LastName', emailTrimmed);

    // UTM fields (need to be changed for each campaign, once yearly)
    fd.append('UTM_Parameter__c', CAMPAIGN_KEY);
    fd.append('Tech_Data_Origin__c', 'W2L');
    fd.append('LeadSource', 'Website');
    fd.append('Source_URL__c', pathname || 'https://smzh.ch/de');

    try {
      const res = await submitLeadFormData(fd);

      if (!res.ok) {
        // Try to show whatever the server actually returned
        const text = await res.text();
        console.error('Leads API error raw:', text);
        let msg = 'Unbekannter Fehler';
        try {
          msg = JSON.parse(text)?.message || msg;
        } catch {}
        throw new Error(msg);
      }

      return true;
    } catch (err: any) {
      console.log(err?.message || 'Senden fehlgeschlagen');
      return false;
    }
  };

  const modalContent = (
    <div>
      {!showSuccessModal && (
        <Modal size={size} ariaLabel={ariaLabel} animation={'fade'} {...props}>
          <div className="modal-content-wrapper relative z-10 rounded-2xl bg-white p-6">
            <div className="from-blue-600/30 absolute inset-0 z-0 rounded-2xl bg-gradient-to-br to-slate-800" />
            <div className="from-blue-400/20 absolute inset-0 z-0 rounded-2xl bg-gradient-to-tl to-transparent" />

            <div className={'body-form relative z-10'}>
              <Modal.Header classNameClose=" !p-0 !text-black">
                <div className={'headline-wrapper mx-auto max-w-[391px]'}>
                  <Headline
                    title={
                      mainHeadline?.title || t('modal.soonCanCreateWatchlist')
                    }
                    variant={'headingThree'}
                    className={
                      'mb-4 text-center text-[26px] font-[400] text-black'
                    }
                    spacing={'small'}
                  >
                    {mainHeadline?.title || t('modal.soonCanCreateWatchlist')}
                  </Headline>
                  <p
                    className={
                      'mb-6 text-center text-[16px] font-[400] text-neutral-500'
                    }
                  >
                    {t('modal.weWillInformYou')}
                  </p>
                </div>
              </Modal.Header>

              <Modal.Body>
                <Formik
                  initialValues={{ email: '' }} // ← just email now
                  validationSchema={emailValidationSchema}
                  onSubmit={async ({ email }, { setSubmitting, resetForm }) => {
                    const ok = await submitEmailData(email);
                    if (ok) {
                      resetForm();
                      setShowSuccessModal(true);
                    }
                    setSubmitting(false);
                  }}
                >
                  {({ setFieldValue }) => {
                    return (
                      <Form className="space-y-6">
                        <div className="w-full">
                          <label
                            htmlFor="recallTime"
                            className="mb-2 block text-sm font-medium text-gray-700"
                          >
                            {t('header.termin.emailLabel')}
                          </label>
                          <Field name="email">
                            {({ field, meta }: any) => (
                              <Input
                                {...field}
                                variant="dark"
                                placeholder={t(
                                  'header.termin.emailPlaceholder',
                                )}
                                type="email"
                                error={meta.touched && meta.error}
                                onChange={(value) =>
                                  setFieldValue('email', value)
                                }
                              />
                            )}
                          </Field>
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="mt-1 text-sm text-red-400"
                          />
                        </div>

                        <div className="!mt-4 flex justify-center">
                          <Button
                            type="submit"
                            variant="primary"
                            className={`w-full justify-center`}
                            name={t('modal.pleaseNotifyMe')}
                          />
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </Modal.Body>
            </div>
          </div>
        </Modal>
      )}

      {showSuccessModal && (
        <EmailSuccessModal onClose={handleSuccessModalClose} />
      )}
    </div>
  );
  if (!mounted) return null;
  return createPortal(modalContent, document.body);
};

export default EmailModal;
